// The application registry: identity, chrome and default geometry for every
// app. Components live in appComponents.tsx so this file stays import-cheap.

import type { IconName } from '../brand/brand';

export type AppId =
  | 'playground'
  | 'miner'
  | 'assistants'
  | 'models'
  | 'prompts'
  | 'files'
  | 'notes'
  | 'sysinfo'
  | 'taskmgr'
  | 'network'
  | 'leaderboard'
  | 'wallet'
  | 'readme'
  | 'settings'
  | 'operator'
  | 'token'
  | 'convert';

export interface AppMeta {
  id: AppId;
  title: string; // window and taskbar title
  label: string; // desktop label
  icon: IconName;
  blurb: string; // Start menu description
  w: number;
  h: number;
  minW: number;
  minH: number;
  dark?: boolean;
}

export const APPS: Record<AppId, AppMeta> = {
  prompts: {id:'prompts',title:'Prompt Library',label:'Prompt Library',icon:'prompts',blurb:'Reusable templates and personal prompts',w:830,h:650,minW:360,minH:360},
  files: {id:'files',title:'My Files',label:'My Files',icon:'files',blurb:'Your notes, text files and saved prompts',w:820,h:620,minW:360,minH:360},
  notes: {id:'notes',title:'Notepad',label:'Notepad',icon:'notes',blurb:'A private place for your next idea',w:840,h:670,minW:360,minH:400},
  token: {id:'token',title:'NimoOS token',label:'Token',icon:'token',blurb:'Official contract, supply and token links',w:780,h:650,minW:360,minH:360},
  convert: {id:'convert',title:'convert',label:'Convert',icon:'convert',blurb:'Exchange tokens and AI credits',w:840,h:720,minW:360,minH:400},
  operator: {id:'operator',title:'operator',label:'operator',icon:'system',blurb:'Private platform overview',w:960,h:660,minW:380,minH:380},
  playground: { id: 'playground', title: 'Playground', label: 'Playground', icon: 'playground', blurb: 'Choose your AI. Chat with mining credits.', w: 1020, h: 690, minW: 380, minH: 400 },
  miner: { id: 'miner', title: 'Miner', label: 'Miner', icon: 'miner', blurb: 'Mine credits with this device', w: 740, h: 540, minW: 420, minH: 320, dark: true },
  assistants: { id: 'assistants', title: 'Assistants', label: 'Assistants', icon: 'assistants', blurb: 'Your instructions. Your AI.', w: 820, h: 640, minW: 380, minH: 380 },
  models: { id: 'models', title: 'Models', label: 'Models', icon: 'models', blurb: 'Choose an AI model and compare credits', w: 860, h: 560, minW: 440, minH: 340 },
  sysinfo: { id: 'sysinfo', title: 'System', label: 'System', icon: 'system', blurb: 'This device, measured', w: 760, h: 540, minW: 400, minH: 320, dark: true },
  taskmgr: { id: 'taskmgr', title: 'Task Manager', label: 'Task Manager', icon: 'tasks', blurb: 'Processes and live hash rates', w: 800, h: 580, minW: 420, minH: 340 },
  network: { id: 'network', title: 'Network', label: 'Network', icon: 'network', blurb: 'Verified miners and AI availability', w: 880, h: 560, minW: 440, minH: 320 },
  leaderboard: { id: 'leaderboard', title: 'Rankings', label: 'Rankings', icon: 'ranks', blurb: 'Top miners by verified work', w: 820, h: 560, minW: 420, minH: 320 },
  wallet: { id: 'wallet', title: 'Credits', label: 'Credits', icon: 'wallet', blurb: 'Address, credits and token', w: 580, h: 580, minW: 360, minH: 360 },
  readme: { id: 'readme', title: 'Docs', label: 'Docs', icon: 'readme', blurb: 'How NimoOS works', w: 640, h: 560, minW: 360, minH: 300 },
  settings: { id: 'settings', title: 'Settings', label: 'Settings', icon: 'settings', blurb: 'Wallpaper, motion and storage', w: 620, h: 600, minW: 380, minH: 340 },
};

export interface Shortcut {
  key: string;
  app: AppId;
  label: string;
  icon: IconName;
  arg?: Record<string, unknown>;
}

/** Desktop shortcuts, with the AI playground first. */
export function desktopShortcuts(_tokenLabel?: string): Shortcut[] {
  const s = (app: AppId): Shortcut => ({ key: app, app, label: APPS[app].label, icon: APPS[app].icon });
  return [
    s('playground'),
    s('assistants'),
    s('models'),
    s('prompts'),
    s('files'),
    s('notes'),
    s('miner'),
    s('wallet'),
    s('network'),
    s('leaderboard'),
    s('sysinfo'),
    s('settings'),
  ];
}

export const START_ORDER: AppId[] = ['playground','assistants','models','prompts','files','notes','miner','wallet','network','leaderboard','sysinfo','settings','taskmgr','readme','token','convert'];
